// Design tokens for JS (MUI theme, sx, inline styles). Same values as the @theme block of
// styles/global.css, which gives the Tailwind classes (bg-primary, text-primary-strong,
// shadow-card, rounded-card...). Keep the two in sync. Values are plain hex on purpose: MUI's
// colour helpers (alpha, darken, getContrastText) cannot read a CSS var().

// Single accent of the app. primaryStrong is for small text and icons on white (#247DFF is only
// 3.9:1 there, #1958B3 is 6.4:1); primarySoft for selected rows, chips and hovers.
export const colors = {
  primary: "#247DFF",
  primaryStrong: "#1958B3",
  primarySoft: "#EAF2FF",
  danger: "#DC2626",
  dangerStrong: "#B91C1C",
  dangerSoft: "#FEF2F2",
  // Status tones: *Strong is the text on *Soft (>= 4.5:1), the base is for dots and icons
  success: "#589E67", // done, green DateChip
  successStrong: "#3F7A4D",
  successSoft: "#EEF5F0",
  warning: "#F59E0B", // due soon, duplicate, package
  warningStrong: "#8A5A00",
  warningSoft: "#FFF7E6",
  violet: "#7C4DDB", // reactivation
  violetStrong: "#6D28D9",
  violetSoft: "#F3E8FF",
  // Marketing Mind (replaces MM_ACCENT and the literal #3D2A6B / #F6F2FD / #F3EEFC)
  mm: "#7C4DDB",
  mmStrong: "#3D2A6B",
  mmSoft: "#F6F2FD",
  // Outline of fields and filter toggles (3.9:1 on white), same as themes.jsx fieldColors.border
  fieldBorder: "#7B818C",
  white: "#FFFFFF",
  // Tailwind gray scale (text-gray-900, border-gray-200...)
  gray50: "#F9FAFB",
  gray100: "#F3F4F6",
  gray200: "#E5E7EB",
  gray300: "#D1D5DB",
  gray400: "#9CA3AF",
  gray500: "#6B7280",
  gray600: "#4B5563",
  gray700: "#374151",
  gray800: "#1F2937",
  gray900: "#111827",
  // Roles of the linked boards (account, graphic designer, strategist): chip bg / text / border
  roles: {
    account: { bg: "#EEF2FF", fg: "#3730A3", border: "#C7D2FE" },
    graphic: { bg: "#FDF2F8", fg: "#9D174D", border: "#FBCFE8" },
    strategist: { bg: "#ECFDF5", fg: "#065F46", border: "#A7F3D0" },
  },
};
// Flat aliases of the role colours (the names of the design spec): same objects
colors.roleAccount = colors.roles.account;
colors.roleGraphic = colors.roles.graphic;
colors.roleStrategist = colors.roles.strategist;

export const shadows = {
  card: "0 1px 2px rgba(16, 24, 40, 0.04)",
  cardHover: "0 6px 16px rgba(16, 24, 40, 0.08)",
  popover: "0 12px 32px -4px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06)",
  // card lifted by a drag (kanban DragOverlay)
  dragOverlay: "0 12px 28px -6px rgba(16, 24, 40, 0.22), 0 2px 6px rgba(16, 24, 40, 0.08)",
};

export const radii = {
  chip: 6, // card chips and tags (CardChip, DateChip)
  control: 8, // buttons, inputs, menu items
  card: 12, // cards, menus, popovers
  dialog: 16,
  pill: 9999,
};

// Stacking order of the app shell. MUI's own defaults: appBar 1100, drawer 1200, modal 1300,
// snackbar 1400, tooltip 1500. The CSS vars (--z-*) carry the same numbers.
export const zIndex = {
  sidebar: 1100,
  appBar: 1200,
  fab: 1250,
  modal: 1300,
  // context menu and its submenus: above modal.jsx (1301) and the dialogs opened from it (1350)
  popover: 1360,
  submenu: 1370,
  snackbar: 1400,
  tooltip: 1500,
};

// Height scale of the controls (buttons, fields, icon buttons), in px. xs: compact, inside cards
// and kanban column headers; md: default; touch: minimum target on coarse pointers.
export const controls = { xs: 28, sm: 32, md: 36, lg: 40, touch: 44 };

const tokens = { colors, shadows, radii, zIndex, controls };
export default tokens;
